import find from 'lodash/find';
import moment, {Moment} from 'moment-tz';

import {gt} from 'app/utils/i18n';

export type VALID_RANGE_TEMPLATE =
  | 'all-time'
  | 'all-time-30-days'
  | 'today'
  | 'yesterday'
  | 'this-week'
  | 'next-week'
  | 'next-7-days'
  | 'next-7-last-30-days'
  | 'last-7-days'
  | 'last-30-days'
  | 'this-month'
  | 'last-month'
  | 'next-month'
  | 'next-30-days'
  | 'this-quarter'
  | 'last-quarter'
  | 'this-year'
  | 'custom';

// These intervals trigger a concrete only response and the dynamic 'next'/ 'previous' are meaningless
// normal pagination works fine.
export const USE_STANDARD_PAGINATION_RANGE = ['all-time', 'all-time-30-days'];

export const getValidRangeTemplate = (range: string): VALID_RANGE_TEMPLATE => {
  const valid: VALID_RANGE_TEMPLATE[] = [
    'all-time',
    'all-time-30-days',
    'today',
    'yesterday',
    'this-week',
    'next-week',
    'next-7-days',
    'next-7-last-30-days',
    'last-7-days',
    'last-30-days',
    'this-month',
    'last-month',
    'next-month',
    'next-30-days',
    'this-quarter',
    'last-quarter',
    'this-year',
    'custom',
  ];
  if (valid.includes(range as VALID_RANGE_TEMPLATE)) {
    return range as VALID_RANGE_TEMPLATE;
  } else {
    return 'all-time';
  }
};
export interface DateRangeType {
  start: string;
  end: string;
}

export interface DateRangeTemplateType {
  key: VALID_RANGE_TEMPLATE;
  label: () => string;
  build: (tz: string, prevDate?: DateRangeType) => DateRangeType;
}
export const dataRangeTemplatesMap: Record<
  VALID_RANGE_TEMPLATE,
  DateRangeTemplateType
> = {
  'all-time': {
    key: 'all-time',
    label: () => gt('All +0 days'),
    build: (tz) => {
      const now = moment().tz(tz);
      return {
        start: moment('1970-01-01').startOf('day').toISOString(),
        end: now.clone().endOf('day').toISOString(),
      };
    },
  },
  'all-time-30-days': {
    key: 'all-time-30-days',
    label: () => gt('All +30 days'),
    build: (tz) => {
      const now = moment().tz(tz);
      return {
        start: moment('1970-01-01').startOf('day').toISOString(),
        end: now.clone().add(30, 'days').endOf('day').toISOString(),
      };
    },
  },
  today: {
    key: 'today',
    label: () => gt('Today'),
    build: (tz) => {
      const now = moment().tz(tz);
      return {
        start: now.clone().startOf('day').toISOString(),
        end: now.clone().endOf('day').toISOString(),
      };
    },
  },
  yesterday: {
    key: 'yesterday',
    label: () => gt('Yesterday'),
    build: (tz) => {
      const yesterday = moment().tz(tz).subtract(1, 'days');
      return {
        start: yesterday.clone().startOf('day').toISOString(),
        end: yesterday.clone().endOf('day').toISOString(),
      };
    },
  },
  'this-week': {
    key: 'this-week',
    label: () => gt('This Week'),
    build: (tz) => {
      const now = moment().tz(tz);
      return {
        start: now.clone().startOf('week').toISOString(),
        end: now.clone().endOf('week').toISOString(),
      };
    },
  },
  'next-week': {
    key: 'next-week',
    label: () => gt('Next Week'),
    build: (tz) => {
      const reference = moment().tz(tz).add(1, 'weeks');
      return {
        start: reference.clone().startOf('week').toISOString(),
        end: reference.clone().endOf('week').toISOString(),
      };
    },
  },
  'next-7-days': {
    key: 'next-7-days',
    label: () => gt('Next 7 days'),
    build: (tz) => {
      const reference = moment().tz(tz);
      return {
        start: reference.clone().startOf('day').toISOString(),
        end: reference.clone().add(7, 'days').endOf('day').toISOString(),
      };
    },
  },
  'last-7-days': {
    key: 'last-7-days',
    label: () => gt('Last 7 Days'),
    build: (tz) => {
      const now = moment().tz(tz);
      const start = now.clone().subtract(7, 'days');
      return {
        start: start.clone().startOf('day').toISOString(),
        end: now.clone().endOf('day').toISOString(),
      };
    },
  },
  'next-7-last-30-days': {
    key: 'next-7-last-30-days',
    label: () => gt('Next 7 Days and Last 30 days'),
    build: (tz) => {
      const now = moment().tz(tz);
      const start = now.clone().subtract(30, 'days');
      return {
        start: start.clone().startOf('day').toISOString(),
        end: now.clone().add(7, 'days').endOf('day').toISOString(),
      };
    },
  },
  'last-30-days': {
    key: 'last-30-days',
    label: () => gt('Last 30 Days'),
    build: (tz) => {
      const now = moment().tz(tz);
      const start = now.clone().subtract(30, 'days');
      return {
        start: start.clone().startOf('day').toISOString(),
        end: now.clone().endOf('day').toISOString(),
      };
    },
  },
  'this-month': {
    key: 'this-month',
    label: () => gt('This Month'),
    build: (tz) => {
      const now = moment().tz(tz);
      return {
        start: now.clone().startOf('month').toISOString(),
        end: now.clone().endOf('month').toISOString(),
      };
    },
  },
  'last-month': {
    key: 'last-month',
    label: () => gt('Last Month'),
    build: (tz) => {
      const reference = moment().tz(tz).subtract(1, 'months');
      return {
        start: reference.clone().startOf('month').toISOString(),
        end: reference.clone().endOf('month').toISOString(),
      };
    },
  },
  'next-month': {
    key: 'next-month',
    label: () => gt('Next Month'),
    build: (tz) => {
      const reference = moment().tz(tz).add(1, 'months');
      return {
        start: reference.clone().startOf('month').toISOString(),
        end: reference.clone().endOf('month').toISOString(),
      };
    },
  },
  'next-30-days': {
    key: 'next-30-days',
    label: () => gt('Next 30 days'),
    build: (tz) => {
      const reference = moment().tz(tz);
      return {
        start: reference.clone().startOf('day').toISOString(),
        end: reference.clone().add(30, 'days').endOf('day').toISOString(),
      };
    },
  },
  'this-quarter': {
    key: 'this-quarter',
    label: () => gt('This Quarter'),
    build: (tz) => {
      const now = moment().tz(tz);
      return {
        start: now.clone().startOf('quarter').toISOString(),
        end: now.clone().endOf('quarter').toISOString(),
      };
    },
  },
  'last-quarter': {
    key: 'last-quarter',
    label: () => gt('Last Quarter'),
    build: (tz) => {
      const reference = moment().tz(tz).subtract(1, 'quarter');
      return {
        start: reference.clone().startOf('quarter').toISOString(),
        end: reference.clone().endOf('quarter').toISOString(),
      };
    },
  },
  'this-year': {
    key: 'this-year',
    label: () => gt('This Year'),
    build: (tz) => {
      const now = moment().tz(tz);
      return {
        start: now.clone().startOf('year').toISOString(),
        end: now.clone().endOf('year').toISOString(),
      };
    },
  },
  custom: {
    key: 'custom',
    label: () => gt('Custom Range'),
    build: (tz, prevDate) => {
      const now = moment().tz(tz);
      return prevDate != null && prevDate.start !== '' && prevDate.end !== ''
        ? prevDate
        : {
            start: now.clone().subtract(3, 'days').startOf('day').toISOString(),
            end: now.clone().endOf('day').toISOString(),
          };
    },
  },
};
export const humanizedInterval = (start: Moment, end: Moment): string => {
  const year = start.format('YYYY');
  const startMonth = start.format('MMM');
  const startDay = start.format('D');
  const endMonth = end.format('MMM');
  const endDay = end.format('D');

  if (start.year() === end.year()) {
    if (start.month() === end.month()) {
      if (start.date() === end.date()) {
        return start.format('ll');
      }

      return gt('%(month)s %(startDay)s - %(endDay)s, %(year)s', {
        month: startMonth,
        startDay,
        endDay,
        year,
      });
    }

    return gt(
      '%(startMonth)s %(startDay)s - %(endMonth)s %(endDay)s, %(year)s',
      {
        startMonth,
        startDay,
        endMonth,
        endDay,
        year,
      }
    );
  }

  return `${start.format('ll')} - ${end.format('ll')}`;
};

export interface NamedRangeType {
  name: (start: Moment, end: Moment) => string;
  count: number;
  unit: 'day' | 'isoweek' | 'month';
  matches?: (start: Moment, end: Moment) => boolean;
}
export function findIntervalType(start: Moment, end: Moment): NamedRangeType {
  const getFallback = () => {
    // range includes start and end, so we need adds 1
    const days = end.diff(start, 'days') + 1;
    return {
      name: (start: Moment, end: Moment) => humanizedInterval(start, end),
      count: days,
      unit: 'day' as const,
    };
  };

  const intervals = [
    {
      name: (start: Moment, end: Moment) =>
        `${humanizedInterval(start, end)} week`,
      count: 1,
      unit: 'isoweek' as const,
      matches: (start: Moment, end: Moment) => {
        return (
          start.format('GGWW') === end.format('GGWW') &&
          start.isoWeekday() === 1 && // monday
          end.isoWeekday() === 7
        );
      },
    },
    {
      name: (start: Moment) => start.format('MMMM YYYY'),
      count: 1,
      unit: 'month' as const,
      matches: (start: Moment, end: Moment) =>
        start.format('MMYY') === end.format('MMYY') &&
        start.date() === 1 &&
        start.clone().endOf('month').date() === end.date(),
    },
  ];

  return (
    find(intervals, (interval) => interval.matches(start, end)) || getFallback()
  );
}

export const getDateRangeTitle = (
  dateRange: DateRangeType,
  userTz: string
): string => {
  const start = moment(dateRange.start).tz(userTz);
  const end = moment(dateRange.end).tz(userTz);
  return dateRange.start !== '' && dateRange.end !== ''
    ? findIntervalType(start, end).name(start, end)
    : '';
};
